import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "./api.js";
import { useToast } from "./ToastContext.jsx";
import { useUserCache } from "./UserCacheContext.jsx";
import { genUUID } from "./utils.js";

export function adaptRequest(r) {
  if (!r) return null;
  return {
    id: String(r.id),
    summary: r.summary,
    description: r.description,
    type: r.type,
    module: r.module_id ?? r.module,
    status: r.status_id ?? r.status?.id ?? r.statusId ?? r.status,
    // Server-authoritative IsClosed-OR-IsFinalClose verdict (see RequestSummaryDto) -
    // undefined when a call site's DTO didn't set it (older/other endpoints), so
    // consumers that need a fallback should still check their own status lookup.
    closed: r.closed,
    priority: r.priority,
    createdById: r.created_by_id ?? null,
    reporterId:
      r.reporter_id ??
      (typeof r.reporter === "string" ? r.reporter : r.reporter?.id) ??
      null,
    assigneeId:
      r.assignee_id ??
      (typeof r.assignee === "string" ? r.assignee : r.assignee?.id) ??
      null,
    bpId: r.bp_id ?? null,
    bpName: r.bp_name ?? null,
    contactId: r.contact_id ?? null,
    contactName: r.contact_name ?? null,
    cluster: r.cluster ?? null,
    // Every currently-active cluster membership (board/list rows) - plural and
    // distinct from `cluster` above (the single-request detail response's own
    // "current cluster" field). A request can genuinely be in more than one
    // cluster at once - see RequestSummaryDto.clusters's doc comment. Empty
    // when afsp.afts isn't installed, since no provider ever populates it then.
    clusters: Array.isArray(r.clusters) ? r.clusters.map((c) => ({ id: c.id, name: c.name })) : [],
    docNumber: r.document_no ?? r.doc_number ?? r.docNumber ?? null,
    createdAt: r.created_at ?? r.createdAt,
    updatedAt: r.updated_at ?? r.updatedAt,
    progress: r.progress ?? 0,
    comments: r.comment_count ?? r.counts?.comments ?? r.comments ?? 0,
    attachmentCount: r.attachment_count ?? r.attachmentCount ?? 0,
    relatedId: r.related_id ?? r.relatedId ?? null,
    // Requests related to this one (pointing at it via R_RequestRelated_ID) - shown as
    // small tabs on the ticket in the board and cards views, and on the Linked tab.
    related: (Array.isArray(r.related) ? r.related : []).map((x) => ({
      id: x.id,
      docNumber: x.document_no ?? x.docNumber ?? null,
      summary: x.summary,
    })),
    // Quote: expected hours / amount filled in by ActFact, and whether an invoice was made.
    qtyPlan: r.qty_plan ?? r.qtyPlan ?? null,
    requestAmt: r.request_amt ?? r.requestAmt ?? null,
    billable: r.billable ?? false,
    suggestedDiscount: r.suggested_discount ?? r.suggestedDiscount ?? null,
    fixedIn: r.fixed_in_release ?? r.fixedIn ?? null,
    activity: r.activity ?? [],
    availableTransitions: r.available_transitions ?? null,
    attachments: Array.isArray(r.attachments) ? r.attachments : [],
    resolutions: Array.isArray(r.resolutions)
      ? r.resolutions.map((res) => ({
          resolutionId: res.id,
          productName: res.product_name,
          versionLabel: res.version_label,
        }))
      : [],
  };
}

function adaptActivity(e) {
  if (!e) return null;
  return {
    id: e.id,
    kind: e.kind,
    by: e.actor?.id ?? e.by,
    actor: e.actor ?? null,
    at: e.created_at ?? e.at,
    text:
      e.text ?? e.comment ?? e.body ?? e.message ?? e.content ?? e.note ?? "",
    from: e.from_status_id ?? e.old_value ?? e.from ?? null,
    to: e.to_status_id ?? e.new_value ?? e.to ?? null,
    field: e.field ?? null,
    internal: e.internal ?? false,
    attachments: e.attachments ?? [],
  };
}

// Builds the query string shared by the first page and "load more" calls.
// `filters` mirrors what RequestFilters produces: { status, type, priority, q, range, scope }
function buildListQuery(filters, { limit, cursor } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit ?? 25));
  params.set("sort", "-updated_at");

  // "waiting" is "mine" narrowed to WAITING_FOR_ME_STATUS_VALUES client-side (applyFilters).
  if (filters?.scope) params.set("scope", filters.scope === "waiting" ? "mine" : filters.scope);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.type) params.set("type", filters.type);
  if (filters?.priority) params.set("priority", filters.priority);
  if (filters?.q) params.set("q", filters.q);
  if (filters?.range) params.set("range", filters.range);
  if (filters?.bpartnerId) params.set("bpartner_id", filters.bpartnerId);
  if (filters?.clusterId) params.set("cluster_id", filters.clusterId);
  if (cursor) params.set("cursor", cursor);

  return params.toString();
}

// Serializes filters into a stable key so the effect only re-fires on real changes.
function filtersKey(filters) {
  return JSON.stringify({
    scope: filters?.scope || "",
    status: filters?.status || "",
    type: filters?.type || "",
    priority: filters?.priority || "",
    q: filters?.q || "",
    range: filters?.range || "",
    bpartnerId: filters?.bpartnerId || "",
    clusterId: filters?.clusterId || "",
  });
}

export function useRequests(filters) {
  const { addToast } = useToast();
  const { seed } = useUserCache();

  const [openRequests, setOpenRequests] = useState([]);
  const [closedRequests, setClosedRequests] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [closedTotal, setClosedTotal] = useState(0);

  const [nextClosedCursor, setNextClosedCursor] = useState(null);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const isFetchingRef = useRef(false);
  const isCreatingRef = useRef(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef(null);

  const requests = [...openRequests, ...closedRequests].filter(Boolean);
  // Mirrors for fetchDetail to read the latest list data without adding them to its
  // own dependency array (same pattern as filtersRef above).
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const searchResultsRef = useRef(searchResults);
  searchResultsRef.current = searchResults;

  // Refetch from scratch whenever the filter set changes (status/type/priority/q/range/scope).
  useEffect(() => {
    const controller = new AbortController();
    setLoadingInitial(true);
    setNextClosedCursor(null);

    const qs = buildListQuery(filters, { limit: 25 });
    apiFetch(`/requests?${qs}`, { signal: controller.signal })
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.data ?? []);
        const adapted = list.map(adaptRequest).filter(Boolean);

        if (data.next_cursor) {
          setOpenRequests(adapted.slice(0, -25));
          setClosedRequests(adapted.slice(-25));
        } else {
          setOpenRequests(adapted);
          setClosedRequests([]);
        }

        setNextClosedCursor(data.next_cursor ?? null);
        setClosedTotal(data.closed_total ?? 0);
      })
      .catch((err) => {
        if (err.name !== "AbortError") addToast("Could not load requests.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingInitial(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey(filters)]);

  const search = useCallback(
    async (q) => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const trimmed = (q || "").trim();
      if (!trimmed) {
        setSearchResults(null);
        setSearchLoading(false);
        return;
      }
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      setSearchLoading(true);
      try {
        let qs = `q=${encodeURIComponent(trimmed)}&limit=50`;
        // Search otherwise ignores the active filters entirely (unlike the plain
        // list, which goes through buildListQuery) - the cluster filter is the
        // one exception, so searching mid-filter doesn't silently drop it.
        if (filtersRef.current?.clusterId) {
          qs += `&cluster_id=${encodeURIComponent(filtersRef.current.clusterId)}`;
        }
        const data = await apiFetch(
          `/requests/search?${qs}`,
          { signal: ctrl.signal },
        );
        const list = Array.isArray(data) ? data : (data.data ?? []);
        setSearchResults(list.map(adaptRequest).filter(Boolean));
      } catch (err) {
        if (err.name !== "AbortError") {
          setSearchResults([]);
          addToast("Search failed — showing no results.");
        }
      } finally {
        if (!ctrl.signal.aborted) setSearchLoading(false);
      }
    },
    [addToast],
  );

  // Detail-page URLs use docNumber when a request has one (falls back to id
  // otherwise - see requestUrlKey in utils.js) - this resolves that URL segment
  // back to the real internal id fetchDetail needs. A docNumber isn't guaranteed
  // unique (it references an unrelated ERP document), so this can theoretically
  // resolve to the wrong request if two requests share one; that tradeoff was
  // accepted in favor of human-readable/shareable URLs.
  //
  // docNumber is free-form - it can be all-digits and collide in *shape* with an
  // internal id (e.g. both "12345"). So a numeric ref is never assumed to be an
  // id outright: docNumber resolution (local, then remote search) always runs
  // first, and only an unmatched numeric ref falls back to being treated as a
  // raw id. Getting this order backwards on a cold load (nothing cached yet)
  // could otherwise silently open the wrong request.
  const resolveRequestRef = useCallback(async (ref) => {
    if (!ref) return null;

    const localMatch =
      requestsRef.current.find((r) => r?.docNumber === ref) ??
      searchResultsRef.current?.find((r) => r?.docNumber === ref);
    if (localMatch) return localMatch.id;

    try {
      const data = await apiFetch(`/requests/search?q=${encodeURIComponent(ref)}&limit=50`);
      const list = Array.isArray(data) ? data : (data.data ?? []);
      const match = list.map(adaptRequest).filter(Boolean).find((r) => r.docNumber === ref);
      if (match) return match.id;
    } catch {
      // search failing shouldn't block the raw-id fallback below
    }

    return /^\d+$/.test(ref) ? ref : null;
  }, []);

  const moveRequest = useCallback(
    (id, newStatus, comment, confidentialType) => {
      const now = new Date().toISOString();
      let prevStatus;

      const performUpdate = (rs) => {
        const r = rs.find((x) => x?.id === id);
        if (r) prevStatus = r.status;
        return rs.map((x) =>
          x?.id === id ? { ...x, status: newStatus, updatedAt: now } : x,
        );
      };

      setOpenRequests((rs) => performUpdate(rs));
      setClosedRequests((rs) => performUpdate(rs));
      setSearchResults((srs) => (srs ? performUpdate(srs) : null));

      return apiFetch(`/requests/${id}/transitions`, {
        method: "PUT",
        body: JSON.stringify({
          to_status_id: newStatus,
          ...(comment ? { comment } : {}),
          ...(confidentialType ? { confidentialType } : {}),
        }),
      }).catch(() => {
        const performRollback = (rs) =>
          rs.map((x) => (x?.id === id ? { ...x, status: prevStatus } : x));

        setOpenRequests((rs) => performRollback(rs));
        setClosedRequests((rs) => performRollback(rs));
        setSearchResults((srs) => (srs ? performRollback(srs) : null));

        addToast("Could not change request status.");
      });
    },
    [addToast],
  );

  const updateRequest = useCallback(
    (id, patch) => {
      const applyPatch = (rs) =>
        rs.map((r) => (r?.id === id ? { ...r, ...patch } : r));

      setOpenRequests((rs) => applyPatch(rs));
      setClosedRequests((rs) => applyPatch(rs));
      setSearchResults((srs) => (srs ? applyPatch(srs) : null));
      setDetailCache((prev) =>
        prev[String(id)]
          ? { ...prev, [String(id)]: { ...prev[String(id)], ...patch } }
          : prev,
      );

      const api = {};
      if ("summary" in patch) api.summary = patch.summary;
      if ("description" in patch) api.description = patch.description;
      if ("priority" in patch) api.priority = patch.priority;
      if ("type" in patch) api.type = patch.type;
      if ("module" in patch) api.module_id = patch.module;
      if ("docNumber" in patch) api.document_no = patch.docNumber;
      if ("fixedIn" in patch) api.fixed_in_release = patch.fixedIn;
      if ("assigneeId" in patch) api.assignee_id = patch.assigneeId;
      if ("relatedId" in patch) api.related_id = patch.relatedId;
      if ("status" in patch) api.status_id = patch.status;
      if ("clusterId" in patch) api.cluster_id = patch.clusterId;
      if ("confidentialType" in patch) api.confidentialType = patch.confidentialType;
      if ("comment" in patch) api.comment = patch.comment;
      if ("qtyPlan" in patch) api.qty_plan = patch.qtyPlan;
      if ("requestAmt" in patch) api.request_amt = patch.requestAmt;
      if (Object.keys(api).length > 0) {
        return apiFetch(`/requests/${id}`, {
          method: "PATCH",
          body: JSON.stringify(api),
        }).catch(() => addToast("Changes could not be saved to the server."));
      }
      return Promise.resolve();
    },
    [addToast],
  );

  const postTransition = useCallback(
    (id, to, comment, confidentialType) => {
      return apiFetch(`/requests/${id}/transitions`, {
        method: "PUT",
        body: JSON.stringify({
          to_status_id: to,
          ...(comment ? { comment } : {}),
          ...(confidentialType ? { confidentialType } : {}),
        }),
      }).catch((err) => {
        addToast("Status transition could not be recorded.");
        throw err;
      });
    },
    [addToast],
  );

  const createRequest = useCallback(
    async (draft) => {
      // Defense against double-submit (spam-clicking Create): a second call while
      // one is still in flight is a no-op rather than firing a duplicate POST.
      if (isCreatingRef.current) return;
      isCreatingRef.current = true;

      try {
        const fd = new FormData();
        fd.append("summary", draft.summary.trim());
        fd.append("description", draft.description?.trim() || "");
        fd.append("type", draft.type);
        fd.append("priority", draft.priority || "normal");

        if (draft.module) fd.append("module_id", draft.module);
        if (draft.docNumber) fd.append("document_no", draft.docNumber);
        if (draft.bpartnerId) fd.append("bpartner_id", draft.bpartnerId);
        if (draft.contactId) fd.append("contact_id", draft.contactId);
        if (draft.clusterId) fd.append("cluster_id", draft.clusterId);
        // Picked in the duplicate check: the new request is related to this one.
        if (draft.relatedTo?.id) fd.append("related_request_id", draft.relatedTo.id);

        if (draft.attachments && draft.attachments.length > 0) {
          draft.attachments.forEach((file) => {
            fd.append("attachments", file);
          });
        }

        const created = await apiFetch("/requests", {
          method: "POST",
          headers: { "Idempotency-Key": genUUID() },
          body: fd,
        });

        const adapted = adaptRequest(created);
        if (adapted) {
          const relatedId = draft.relatedTo?.id;
          const ref = { id: adapted.id, docNumber: adapted.docNumber, summary: adapted.summary };
          const addRelated = (rs) =>
            relatedId
              ? rs.map((r) => (r?.id === String(relatedId) ? { ...r, related: [...(r.related ?? []), ref] } : r))
              : rs;
          setOpenRequests((rs) => [adapted, ...addRelated(rs)]);
          setSearchResults((srs) => (srs ? [adapted, ...srs] : null));
        }
        return adapted;
      } catch (err) {
        addToast("Could not create the request.");
        throw err;
      } finally {
        isCreatingRef.current = false;
      }
    },
    [addToast],
  );

  // Duplicate check for the create wizard. Never blocks ticket creation: any
  // API failure resolves to "no similar requests".
  const findSimilar = useCallback(async (draft) => {
    try {
      const data = await apiFetch("/requests/similar", {
        method: "POST",
        body: JSON.stringify({
          summary: draft.summary,
          description: draft.description,
        }),
      });
      return (data?.data ?? []).map((r) => ({
        id: r.id,
        docNumber: r.document_no ?? null,
        summary: r.summary,
        canView: !!r.can_view,
      }));
    } catch {
      return [];
    }
  }, []);

  const addComment = useCallback(
    async (id, payload) => {
      const { text, confidentialType = "A", files = [] } = payload || {};
      const fd = new FormData();
      fd.append("text", text);
      fd.append("confidentialType", confidentialType);
      if (files && files.length > 0) {
        files.forEach((file) => {
          fd.append("file", file);
        });
      }

      try {
        await apiFetch(`/requests/${id}/comments`, {
          method: "POST",
          body: fd,
        });

        // The single-request detail endpoint has no comment_count field (only the
        // list endpoint's batch query computes one), so fetchDetail's cache-merge
        // can never pick up the new count from a refresh — bump it locally instead.
        const bumpComments = (rs) =>
          rs.map((r) => (r?.id === id ? { ...r, comments: (r.comments ?? 0) + 1 } : r));
        setOpenRequests((rs) => bumpComments(rs));
        setClosedRequests((rs) => bumpComments(rs));
        setSearchResults((srs) => (srs ? bumpComments(srs) : null));
        return true;
      } catch (error) {
        addToast("Comment could not be posted to the server.");
        return false;
      }
    },
    [addToast],
  );

  const loadMoreClosed = useCallback(async () => {
    if (!nextClosedCursor || loadingClosed || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoadingClosed(true);

    try {
      const qs = buildListQuery(filtersRef.current, {
        limit: 25,
        cursor: nextClosedCursor,
      });
      const data = await apiFetch(`/requests?${qs}`);
      const list = Array.isArray(data) ? data : (data.data ?? []);

      setClosedRequests((prev) => {
        const combined = [...prev, ...list.map(adaptRequest)];
        const validItems = combined.filter((item) => item && item.id);
        return Array.from(
          new Map(validItems.map((item) => [item.id, item])).values(),
        );
      });

      setNextClosedCursor(data.next_cursor ?? null);
      // closed_total still reflects the first page's count, which is correct
      // since it's computed from the same filters and doesn't change per-page.
    } catch {
      addToast("Could not load more requests.");
    } finally {
      isFetchingRef.current = false;
      setLoadingClosed(false);
    }
  }, [nextClosedCursor, loadingClosed, addToast]);

  const fetchDetail = useCallback(
    async (id) => {
      try {
        const [detail, activityResp] = await Promise.all([
          apiFetch(`/requests/${id}`),
          apiFetch(`/requests/${id}/activity`),
        ]);
        const rawEvents = Array.isArray(activityResp)
          ? activityResp
          : (activityResp.data ?? []);
        seed(rawEvents.map((e) => e?.actor).filter(Boolean));
        const events = rawEvents.map(adaptActivity).filter(Boolean);
        const detailReq = adaptRequest(detail);

        if (!detailReq) return;

        setDetailCache((prev) => {
          const cached = prev[String(id)];
          // A fresh visit from the board/list has no prior detailCache entry, but
          // openRequests/closedRequests/searchResults already has the correct count from the
          // list fetch — fall back to that before accepting the single-detail endpoint's
          // unreliable (often 0) value.
          const listMatch =
            requestsRef.current.find((r) => r?.id === String(id)) ??
            searchResultsRef.current?.find((r) => r?.id === String(id));
          return {
            ...prev,
            [String(id)]: {
              ...detailReq,
              // The single-request endpoint doesn't reliably populate attachmentCount, so a
              // second (e.g. StrictMode double-invoked) fetchDetail call must not clobber a
              // value AttachmentsTab already corrected in the cache with a fresh, zeroed one.
              attachmentCount:
                detailReq.attachmentCount > 0
                  ? detailReq.attachmentCount
                  : (cached?.attachmentCount ?? listMatch?.attachmentCount ?? detailReq.attachmentCount),
              activity: events,
            },
          };
        });

        const mergeDetails = (rs) =>
          rs.map((r) =>
            r?.id === String(id)
              ? {
                  ...r,
                  ...detailReq,
                  comments:
                    detailReq.comments > 0 ? detailReq.comments : r.comments,
                  attachments:
                    detailReq.attachments?.length > 0
                      ? detailReq.attachments
                      : r.attachments,
                  attachmentCount:
                    detailReq.attachmentCount > 0
                      ? detailReq.attachmentCount
                      : r.attachmentCount,
                  // The single-request detail endpoint has no `clusters` (plural) field at
                  // all - only `cluster` (singular, detail-panel-only) - so adaptRequest
                  // always defaults detailReq.clusters to []. Keep the list row's own value
                  // (from the batch list/board fetch) instead of letting that empty array
                  // wipe the Kanban card's cluster badge every time detail is opened.
                  clusters: r.clusters,
                  activity: events,
                }
              : r,
          );

        setOpenRequests((rs) => mergeDetails(rs));
        setClosedRequests((rs) => mergeDetails(rs));
        setSearchResults((srs) => (srs ? mergeDetails(srs) : null));
      } catch (err) {
        if (err.name !== "AbortError")
          addToast("Could not load request details.");
      }
    },
    [addToast, seed],
  );

  const applySSEEvent = useCallback((type, data) => {
    if (!data) return;
    if (type === "request.updated") {
      const adapted = adaptRequest(data);
      if (!adapted) return;
      const updateList = (rs) =>
        rs.map((r) =>
          r?.id === data.id
            ? {
                ...r,
                ...adapted,
                attachments:
                  adapted.attachments?.length > 0
                    ? adapted.attachments
                    : r.attachments,
                attachmentCount:
                  adapted.attachmentCount > 0
                    ? adapted.attachmentCount
                    : r.attachmentCount,
                // The SSE payload carries no link data; keep what we have.
                relatedId: "related_id" in data ? adapted.relatedId : r.relatedId,
                related: Array.isArray(data.related) ? adapted.related : r.related,
              }
            : r,
        );

      setOpenRequests((rs) => updateList(rs));
      setClosedRequests((rs) => updateList(rs));
      setSearchResults((srs) => (srs ? updateList(srs) : null));
      setDetailCache((prev) => {
        if (!prev[String(data.id)]) return prev;
        return {
          ...prev,
          [String(data.id)]: {
            ...prev[String(data.id)],
            ...adapted,
            attachments:
              adapted.attachments?.length > 0
                ? adapted.attachments
                : prev[String(data.id)].attachments,
            attachmentCount:
              adapted.attachmentCount > 0
                ? adapted.attachmentCount
                : prev[String(data.id)].attachmentCount,
          },
        };
      });
    } else if (type === "request.activity") {
      const updateActivity = (rs) =>
        rs.map((r) =>
          r?.id === data.request_id
            ? {
                ...r,
                activity: [...(r.activity || []), data.event].filter(Boolean),
                updatedAt: data.event?.at ?? r.updatedAt,
              }
            : r,
        );

      setOpenRequests((rs) => updateActivity(rs));
      setClosedRequests((rs) => updateActivity(rs));
      setSearchResults((srs) => (srs ? updateActivity(srs) : null));
      setDetailCache((prev) => {
        if (!prev[String(data.request_id)]) return prev;
        const r = prev[String(data.request_id)];
        return {
          ...prev,
          [String(data.request_id)]: {
            ...r,
            activity: [...(r.activity || []), data.event].filter(Boolean),
            updatedAt: data.event?.at ?? r.updatedAt,
          },
        };
      });
    }
  }, []);

  return {
    requests,
    openRequests,
    closedRequests,
    detailCache,
    closedTotal,
    hasMore: nextClosedCursor !== null,
    loadMore: loadMoreClosed,
    loadingMore: loadingClosed,
    loadingInitial,
    search,
    searchResults,
    searchLoading,
    moveRequest,
    updateRequest,
    postTransition,
    createRequest,
    findSimilar,
    addComment,
    applySSEEvent,
    fetchDetail,
    resolveRequestRef,
  };
}